<!-- My kitchens. THIS FILE IS YOURS.

     The kitchen on screen is autosaved; this is for the ones a person wants to KEEP -- the
     one before they tried the U, the one with oak, the one for the other house. Save under a
     name, open one, rename, delete, start again. Each opens as one Undo step over what was
     there, so opening the wrong one costs nothing. -->
<template>
  <div class="scrim" @click.self="$emit('close')">
    <section class="card" role="dialog" aria-modal="true" aria-labelledby="kitchens-title">
      <header>
        <h1 id="kitchens-title">My kitchens</h1>
        <button class="x" type="button" aria-label="Back to the kitchen" @click="$emit('close')">
          <IonIcon :icon="closeOutline" />
        </button>
      </header>

      <form class="save" @submit.prevent="save">
        <input v-model.trim="name" type="text" maxlength="60" enterkeyhint="done"
               :placeholder="suggested" aria-label="A name for this kitchen" />
        <button type="submit" :disabled="busy">Save this one</button>
      </form>
      <p class="fine">The kitchen you are working on is kept on this device as you go. Saving names a copy to come back to.</p>

      <!-- ── AS SCANNED, pinned above everything ────────────────────────────────────
           Not a saved kitchen: a record of a room. It is at the top because it is the thing
           every design below it was drawn from, and because it is the one thing in here
           that cannot be made again -- the kitchen it describes is being pulled out. -->
      <section class="originals" v-if="originals.length">
        <h2>As scanned</h2>
        <p class="fine">The room as the scan found it. It is never edited; designs are made from it.</p>
        <ul class="list">
          <li v-for="o in originals" :key="o.id">
            <button type="button" class="open orig" @click="$emit('look', o)">
              <span class="text">
                <span class="name">{{ o.name }}</span>
                <span class="when">{{ when(Date.parse(o.scannedAt)) }} · {{ designWord(o.id) }}</span>
              </span>
            </button>
            <span class="acts">
              <button type="button" class="make" @click="$emit('design', o)">New design</button>
              <button type="button" class="del" @click="dropOriginal(o)" aria-label="Delete this scan">
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 6h12"/><path d="M8 6V4h4v2"/><path d="M6 6l1 10h6l1-10"/></svg>
              </button>
            </span>
          </li>
        </ul>
      </section>

      <ul class="list" v-if="list.length">
        <li v-for="k in list" :key="k.id">
          <button type="button" class="open" @click="open(k)">
            <span class="thumb" v-html="k.thumb" />
            <span class="text">
              <span class="name">{{ k.name }}</span>
              <span class="when">{{ k.cabinets }} cabinets · {{ when(k.savedAt) }}</span>
              <span class="from" v-if="fromWhat(k)">{{ fromWhat(k) }}</span>
            </span>
          </button>
          <span class="acts">
            <button type="button" @click="rename(k)" aria-label="Rename">
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 16h3l8.5-8.5a2 2 0 0 0-3-3L4 13v3Z"/></svg>
            </button>
            <button type="button" class="del" @click="del(k)" aria-label="Delete">
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 6h12"/><path d="M8 6V4h4v2"/><path d="M6 6l1 10h6l1-10"/></svg>
            </button>
          </span>
        </li>
      </ul>
      <p class="none" v-else-if="loaded">Nothing saved yet.</p>

      <div class="row">
        <button type="button" class="quiet" @click="fresh">
          <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 4v12"/><path d="M4 10h12"/></svg>
          Start a new kitchen
        </button>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { IonIcon } from '@ionic/vue';
import { closeOutline } from 'ionicons/icons';
import { listKitchens, saveKitchen, loadKitchen, deleteKitchen, renameKitchen,
  listOriginals, deleteOriginal } from '../kitchen/store.js';

const props = defineProps({ plan: Object, maker: Object });
const emit = defineEmits(['close', 'open', 'fresh', 'said', 'named', 'design', 'look']);

const list = ref([]);
const originals = ref([]);
const loaded = ref(false);
const busy = ref(false);
const name = ref(props.plan.name ?? '');
const suggested = `${{ single: 'One-wall', galley: 'Galley', l: 'L-shaped', u: 'U-shaped' }[props.plan.shape] ?? 'My'} kitchen`;

async function refresh() {
  [list.value, originals.value] = await Promise.all([listKitchens(), listOriginals()]);
  loaded.value = true;
}
onMounted(refresh);

/** The saved designs drawn from one scanned room. Counted off the list, not opened. */
const designsFrom = (id) => list.value.filter((k) => k.originalId === id);
const designWord = (id) => {
  const n = designsFrom(id).length;
  return n === 0 ? 'no designs yet' : n === 1 ? '1 design' : `${n} designs`;
};
/** What a saved design says about where it came from, when it came from anywhere. */
function fromWhat(k) {
  const o = originals.value.find((x) => x.id === k.originalId);
  return o ? `from ${o.name} · ${when(Date.parse(o.scannedAt))}` : null;
}

/*
 * Deleting a scan, which is the one delete in this app that cannot be undone by redoing the
 * work. A saved kitchen can be designed again; a room that has been ripped out cannot be
 * scanned again. So: refused outright while any saved design still points at it -- and it
 * says WHICH, because "you have designs using it" is not something anybody can act on --
 * and behind two confirmations even when nothing does.
 */
async function dropOriginal(o) {
  const using = designsFrom(o.id);
  if (using.length) {
    const names = using.map((k) => `“${k.name}”`).join(', ');
    emit('said', `${names} ${using.length === 1 ? 'was' : 'were'} designed from ${o.name}. Delete ${using.length === 1 ? 'it' : 'them'} first.`);
    return;
  }
  if (!window.confirm(`Delete ${o.name}? This is the record of the room as it is today.`)) return;
  if (!window.confirm('Really delete it? The scan cannot be got back, and the room may have changed by now.')) return;
  await deleteOriginal(o.id);
  await refresh();
  emit('said', `${o.name} deleted.`);
}

async function save() {
  busy.value = true;
  const n = name.value || suggested;
  const id = await saveKitchen({ ...props.plan, name: n }, props.maker, n);
  busy.value = false;
  if (!id) { emit('said', 'This browser would not keep it. Try the share link instead.'); return; }
  emit('named', n);
  emit('said', `Saved as “${n}”.`);
  await refresh();
}
async function open(k) {
  const plan = await loadKitchen(k.id);
  if (!plan) { emit('said', 'That one would not open.'); return; }
  emit('open', plan, k.name);
}
async function rename(k) {
  const n = window.prompt('Call it', k.name);
  if (n === null || !n.trim()) return;
  await renameKitchen(k.id, n.trim());
  await refresh();
}
async function del(k) {
  if (!window.confirm(`Delete “${k.name}”? The kitchen on screen is not affected.`)) return;
  await deleteKitchen(k.id);
  await refresh();
}
function fresh() {
  if (!window.confirm('Start a new kitchen? The one on screen stays one Undo away.')) return;
  emit('fresh');
}
function when(t) {
  if (!t) return '';
  const d = new Date(t);
  const today = new Date();
  const days = Math.round((today - d) / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}
</script>

<style scoped>
.scrim {
  position: fixed; inset: 0; z-index: 60;
  background: color-mix(in oklab, var(--k-ink) 42%, transparent);
  display: flex; align-items: flex-end; justify-content: center;
}
@media (min-width: 700px) { .scrim { align-items: center; padding: 24px; } }
.card {
  width: 100%; max-width: 520px; max-height: 88vh; overflow-y: auto;
  background: var(--k-paper); border-radius: 20px 20px 0 0;
  padding: 6px 20px calc(20px + env(safe-area-inset-bottom));
}
@media (min-width: 700px) { .card { border-radius: 16px; } }
header { display: flex; align-items: center; gap: 10px; padding: 12px 0 8px; }
h1 { flex: 1; margin: 0; font: 600 20px/1.25 var(--ion-font-family, system-ui); color: var(--k-ink); letter-spacing: -.015em; }
.x { border: 0; background: none; color: var(--k-mute); cursor: pointer; width: 40px; height: 40px; display: grid; place-items: center; font-size: 20px; }

.save { display: flex; gap: 8px; }
.save input {
  flex: 1; min-width: 0; border: 1px solid var(--k-line); border-radius: 10px; background: var(--k-paper);
  padding: 12px 13px; min-height: 46px; font: 400 15px/1 var(--ion-font-family, system-ui); color: var(--k-ink);
}
.save input:focus { outline: 2px solid var(--k-ink); outline-offset: 1px; }
.save button {
  border: 0; border-radius: 10px; cursor: pointer; padding: 0 16px; min-height: 46px;
  background: var(--k-ink); color: var(--k-paper); font: 600 13.5px/1 var(--ion-font-family, system-ui);
}
.fine { margin: 8px 0 14px; font: 400 12px/1.4 var(--ion-font-family, system-ui); color: var(--k-mute); }

.list { list-style: none; margin: 0; padding: 0; display: grid; gap: 6px; }
.list li { display: flex; align-items: center; gap: 6px; }
.open {
  flex: 1; min-width: 0; display: flex; align-items: center; gap: 12px; text-align: left;
  padding: 8px; border-radius: 12px; cursor: pointer;
  border: 1px solid var(--k-line); background: var(--k-paper);
}
.open:active { background: var(--k-fill); }
.thumb { flex: 0 0 90px; height: 60px; border-radius: 8px; overflow: hidden; background: var(--k-room); }
.thumb :deep(svg) { display: block; width: 100%; height: 100%; }
.text { flex: 1; min-width: 0; display: grid; gap: 3px; }
.name { font: 600 14px/1.2 var(--ion-font-family, system-ui); color: var(--k-ink); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.when { font: 400 12px/1.2 var(--ion-font-family, system-ui); color: var(--k-mute); }
.acts { display: flex; flex-direction: column; gap: 2px; }
.acts button {
  border: 0; background: none; color: var(--k-mute); cursor: pointer;
  width: 38px; height: 36px; display: grid; place-items: center; border-radius: 8px;
}
.acts button:active { background: var(--k-fill); }
.acts .del { color: var(--k-warn); }
.from { font: 500 11.5px/1.2 var(--ion-font-family, system-ui); color: var(--k-accent); }

.originals { margin: 0 0 16px; }
.originals h2 {
  margin: 0; font: 600 13px/1.2 var(--ion-font-family, system-ui); color: var(--k-ink);
  letter-spacing: .02em; text-transform: uppercase;
}
.originals .fine { margin: 4px 0 8px; }
/* No thumbnail: an Original is a room, and the plan drawing of a room with the old cabinets
   in it reads as one more design, which is the one thing it is not. */
.originals .orig { padding: 12px 12px; background: var(--k-fill); border-style: dashed; }
.originals .acts { flex-direction: row; align-items: center; gap: 4px; }
.originals .make {
  width: auto; padding: 0 12px; height: 36px; border-radius: 8px;
  background: var(--k-ink); color: var(--k-paper);
  font: 600 12px/1 var(--ion-font-family, system-ui);
}
.none { margin: 6px 0; font: 400 13.5px/1.5 var(--ion-font-family, system-ui); color: var(--k-dim); }

.row { margin-top: 10px; }
.quiet {
  display: inline-flex; align-items: center; gap: 8px;
  border: 0; background: none; cursor: pointer; padding: 8px 2px; min-height: 40px;
  font: 500 12.5px/1 var(--ion-font-family, system-ui); color: var(--k-accent);
}
</style>
